import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import happyHourService from '../../../../services/happyHourService';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { menuItemService } from '../../../../services/menuItemService';
import { MenuCategoryDTO, MenuItemListDTO, CreateHappyHourRuleDTO } from '../../../../types';
import { ArrowLeft, Clock, Tag, Percent, DollarSign, AlertCircle } from 'lucide-react';
import '../RestaurantPage.css';
import { getServerErrorMessage } from '../../../../utils/errorUtils';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const HappyHourCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();
  const isEdit   = Boolean(id);

  /* ── Loading / error ── */
  const [loadingData, setLoadingData] = useState(isEdit);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /* ── Reference data ── */
  const [categories, setCategories] = useState<MenuCategoryDTO[]>([]);
  const [menuItems,  setMenuItems]  = useState<MenuItemListDTO[]>([]);
  const [itemSearch, setItemSearch] = useState('');

  /* ── Form state ── */
  const [name,          setName]          = useState('');
  const [description,   setDescription]   = useState('');
  const [activeDays,    setActiveDays]     = useState(0);
  const [timeFrom,      setTimeFrom]       = useState('17:00');
  const [timeTo,        setTimeTo]         = useState('19:00');
  const [discountType,  setDiscountType]   = useState<'Percentage' | 'FixedAmount'>('Percentage');
  const [discountValue, setDiscountValue]  = useState<number | ''>('');
  const [appliesTo,     setAppliesTo]      = useState<'AllItems' | 'SelectedCategories' | 'SelectedItems'>('AllItems');
  const [categoryIds,   setCategoryIds]    = useState<number[]>([]);
  const [menuItemIds,   setMenuItemIds]    = useState<number[]>([]);
  const [isActive,      setIsActive]       = useState(true);

  /* ── Load categories + existing rule on edit ── */
  useEffect(() => {
    menuCategoryService.getAll().then(res => {
      const d = (res.data as any)?.data;
      setCategories(Array.isArray(d) ? d : (d?.data ?? []));
    }).catch(() => {});

    menuItemService.getAll(1, 500).then(res => {
      // getAll returns ApiResponse<PaginatedResponse<MenuItemListDTO>>
      // so the array lives at .data (ApiResponse.data) .data (PaginatedResponse.data)
      const paginated = (res.data as any)?.data;
      const items = paginated?.data ?? paginated?.items ?? [];
      setMenuItems(Array.isArray(items) ? items : []);
    }).catch(() => {});

    if (isEdit && id) {
      setLoadingData(true);
      happyHourService.getById(parseInt(id)).then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setName(d.name ?? '');
        setDescription(d.description ?? '');
        setActiveDays(d.activeDays ?? 0);
        setTimeFrom(d.timeFrom ?? '17:00');
        setTimeTo(d.timeTo ?? '19:00');
        setDiscountType(d.discountType ?? 'Percentage');
        setDiscountValue(d.discountValue ?? '');
        setAppliesTo(d.appliesTo ?? 'AllItems');
        setCategoryIds(d.categoryIds ?? []);
        setMenuItemIds(d.menuItemIds ?? []);
        setIsActive(d.isActive ?? true);
      }).catch(() => setError('Failed to load rule.'))
        .finally(() => setLoadingData(false));
    }
  }, [id]); // eslint-disable-line

  /* ── Day bitmask helpers ── */
  const isDayOn  = (i: number) => activeDays === 0 ? true : !!(activeDays & (1 << i));
  const toggleDay = (i: number, on: boolean) => {
    const bit = 1 << i;
    if (activeDays === 0) setActiveDays(on ? 0 : 127 & ~bit);
    else setActiveDays(on ? activeDays | bit : activeDays & ~bit);
  };

  /* ── Category toggle ── */
  const toggleCategory = (catId: number) =>
    setCategoryIds(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );

  /* ── Menu item toggle ── */
  const toggleMenuItem = (itemId: number) =>
    setMenuItemIds(prev =>
      prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]
    );

  /* ── Validation ── */
  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim())                           errs.name          = 'Required';
    if (discountValue === '' || Number(discountValue) <= 0)
                                                errs.discountValue = 'Must be greater than 0';
    if (discountType === 'Percentage' && Number(discountValue) > 100)
                                                errs.discountValue = 'Cannot exceed 100%';
    if (!timeFrom)                              errs.timeFrom      = 'Required';
    if (!timeTo)                                errs.timeTo        = 'Required';
    if (appliesTo === 'SelectedCategories' && categoryIds.length === 0)
                                                errs.categoryIds   = 'Select at least one category';
    if (appliesTo === 'SelectedItems' && menuItemIds.length === 0)
                                                errs.menuItemIds   = 'Select at least one menu item';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);

    const payload: CreateHappyHourRuleDTO = {
      name:          name.trim(),
      description:   description.trim() || undefined,
      activeDays,
      timeFrom,
      timeTo,
      discountType,
      discountValue: Number(discountValue),
      appliesTo,
      isActive,
      categoryIds: appliesTo === 'SelectedCategories' ? categoryIds : [],
      menuItemIds: appliesTo === 'SelectedItems'       ? menuItemIds : [],
    };

    try {
      if (isEdit && id) await happyHourService.update(parseInt(id), payload);
      else               await happyHourService.create(payload);
      navigate('/restaurant/happy-hours', { state: { message: isEdit ? 'Rule updated.' : 'Rule created.' } });
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? 'Failed to save rule.');
      setSaving(false);
    }
  };

  if (loadingData) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">

          {/* ── Top bar ── */}
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/restaurant/happy-hours')}>
                <ArrowLeft size={13} /> Happy Hours
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Clock size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? 'Edit Happy Hour Rule' : 'New Happy Hour Rule'}
                </h1>
                <p className="ims-page-subtitle">Define a time-based discount window for your menu</p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/happy-hours')} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" /> Saving…</>
                  : isEdit ? 'Update' : 'Save'}
              </button>
            </div>
          </div>

          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          {/* ── Two-column layout ── */}
          <div className="ims-create-body">

            {/* ════ LEFT ════ */}
            <div className="ims-panel-left" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Basic info */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Tag size={14} className="mi-header-icon" />
                  <span className="ims-card-title">Rule Details</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className={`mi-field${fieldErrors.name ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">Rule Name <span className="mi-required">*</span></label>
                    <input
                      type="text" className="mi-input" value={name} maxLength={100} autoFocus
                      onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })); }}
                      placeholder="e.g. Evening Happy Hour"
                    />
                    {fieldErrors.name && <span className="mi-err">{fieldErrors.name}</span>}
                  </div>

                  <div className="mi-field">
                    <label className="mi-label">Description</label>
                    <textarea
                      className="mi-input mi-textarea" value={description} rows={2} maxLength={500}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Optional — shown on the POS banner"
                    />
                  </div>

                  {/* Status toggle */}
                  <label className={`mi-toggle-row${isActive ? ' mi-toggle-on' : ''}`}>
                    <div className="mi-toggle-text">
                      <span className="mi-toggle-label">Active</span>
                      <span className="mi-toggle-sub">Rule is enabled and will fire during its window</span>
                    </div>
                    <div className={`mi-switch${isActive ? ' mi-switch-on' : ''}`} onClick={() => setIsActive(v => !v)}>
                      <div className="mi-switch-thumb" />
                    </div>
                  </label>

                </div>
              </div>

              {/* Discount */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  {discountType === 'Percentage' ? <Percent size={14} className="mi-header-icon" /> : <DollarSign size={14} className="mi-header-icon" />}
                  <span className="ims-card-title">Discount</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className="mi-field">
                    <label className="mi-label">Discount Type</label>
                    <div className="mi-seg-group">
                      {(['Percentage', 'FixedAmount'] as const).map(v => (
                        <button key={v} type="button"
                          className={`mi-seg${discountType === v ? ' mi-seg-active' : ''}`}
                          onClick={() => setDiscountType(v)}>
                          {v === 'Percentage' ? '% Percentage' : '$ Fixed Amount'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={`mi-field${fieldErrors.discountValue ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">
                      {discountType === 'Percentage' ? 'Discount %' : 'Discount Amount'} <span className="mi-required">*</span>
                    </label>
                    <div className="mi-input-affix">
                      <input
                        type="number" className="mi-input mi-input-has-suffix"
                        value={discountValue} min={0.01} step="0.01"
                        max={discountType === 'Percentage' ? 100 : undefined}
                        onChange={e => { setDiscountValue(e.target.value === '' ? '' : parseFloat(e.target.value)); setFieldErrors(p => ({ ...p, discountValue: '' })); }}
                        placeholder="0"
                      />
                      <span className="mi-suffix">{discountType === 'Percentage' ? '%' : '$'}</span>
                    </div>
                    {fieldErrors.discountValue && <span className="mi-err">{fieldErrors.discountValue}</span>}
                  </div>

                </div>
              </div>

            </div>

            {/* ════ RIGHT ════ */}
            <div className="ims-panel-right" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Time window */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Clock size={14} className="mi-header-icon" />
                  <span className="ims-card-title">Time Window</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className="mi-row-2">
                    <div className={`mi-field${fieldErrors.timeFrom ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">From <span className="mi-required">*</span></label>
                      <input type="time" className="mi-input" value={timeFrom}
                        onChange={e => { setTimeFrom(e.target.value); setFieldErrors(p => ({ ...p, timeFrom: '' })); }} />
                      {fieldErrors.timeFrom && <span className="mi-err">{fieldErrors.timeFrom}</span>}
                    </div>
                    <div className={`mi-field${fieldErrors.timeTo ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">To <span className="mi-required">*</span></label>
                      <input type="time" className="mi-input" value={timeTo}
                        onChange={e => { setTimeTo(e.target.value); setFieldErrors(p => ({ ...p, timeTo: '' })); }} />
                      {fieldErrors.timeTo && <span className="mi-err">{fieldErrors.timeTo}</span>}
                    </div>
                  </div>
                  <span className="mi-hint">Times cross midnight if "To" is earlier than "From" (e.g. 22:00–02:00).</span>

                  {/* Days */}
                  <div className="mi-field" style={{ marginTop: 4 }}>
                    <label className="mi-label">Active Days</label>
                    <div className="mi-days-grid">
                      {DAYS.map((day, i) => (
                        <label key={day} className={`mi-day-pill${isDayOn(i) ? ' mi-day-on' : ''}`}>
                          <input type="checkbox" style={{ display: 'none' }} checked={isDayOn(i)} onChange={e => toggleDay(i, e.target.checked)} />
                          {day}
                        </label>
                      ))}
                      <label className={`mi-day-pill mi-day-all${activeDays === 0 ? ' mi-day-all-on' : ''}`}>
                        <input type="checkbox" style={{ display: 'none' }} checked={activeDays === 0}
                          onChange={e => setActiveDays(e.target.checked ? 0 : 127)} />
                        All
                      </label>
                    </div>
                    <span className="mi-hint">All = runs every day of the week</span>
                  </div>

                </div>
              </div>

              {/* Scope */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Tag size={14} className="mi-header-icon" />
                  <span className="ims-card-title">Applies To</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  <div className="mi-seg-group" style={{ flexWrap: 'wrap' }}>
                    {([
                      ['AllItems',           'All Menu Items'],
                      ['SelectedCategories', 'By Category'],
                      ['SelectedItems',      'Individual Items'],
                    ] as const).map(([v, label]) => (
                      <button key={v} type="button"
                        className={`mi-seg${appliesTo === v ? ' mi-seg-active' : ''}`}
                        onClick={() => setAppliesTo(v)}>
                        {label}
                      </button>
                    ))}
                  </div>

                  {appliesTo === 'SelectedCategories' && (
                    <div className="mi-field" style={{ marginTop: 10 }}>
                      <label className="mi-label">Categories <span className="mi-required">*</span></label>
                      {categories.length === 0 ? (
                        <span className="mi-hint">No categories found.</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                          {categories.map(cat => {
                            const on = categoryIds.includes(cat.id);
                            return (
                              <label key={cat.id}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  padding: '4px 10px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                                  border: `1px solid ${on ? 'var(--ims-accent)' : 'var(--border-color)'}`,
                                  background: on ? 'var(--ims-accent)' : 'transparent',
                                  color: on ? '#fff' : 'var(--text-primary)',
                                  fontWeight: on ? 600 : 400,
                                }}>
                                <input type="checkbox" style={{ display: 'none' }} checked={on}
                                  onChange={() => { toggleCategory(cat.id); setFieldErrors(p => ({ ...p, categoryIds: '' })); }} />
                                {cat.name}
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {fieldErrors.categoryIds && <span className="mi-err" style={{ marginTop: 4 }}>{fieldErrors.categoryIds}</span>}
                    </div>
                  )}

                  {appliesTo === 'SelectedItems' && (
                    <div className="mi-field" style={{ marginTop: 10 }}>
                      <label className="mi-label">Menu Items <span className="mi-required">*</span></label>
                      <input
                        type="text" className="mi-input" placeholder="Search items…"
                        value={itemSearch} onChange={e => setItemSearch(e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                      {menuItems.length === 0 ? (
                        <span className="mi-hint">No menu items found.</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                          {menuItems
                            .filter(mi => mi.name.toLowerCase().includes(itemSearch.toLowerCase()))
                            .map(mi => {
                              const on = menuItemIds.includes(mi.id);
                              return (
                                <label key={mi.id}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                    padding: '4px 10px', borderRadius: 99, fontSize: 12, cursor: 'pointer',
                                    border: `1px solid ${on ? 'var(--ims-accent)' : 'var(--border-color)'}`,
                                    background: on ? 'var(--ims-accent)' : 'transparent',
                                    color: on ? '#fff' : 'var(--text-primary)',
                                    fontWeight: on ? 600 : 400,
                                  }}>
                                  <input type="checkbox" style={{ display: 'none' }} checked={on}
                                    onChange={() => { toggleMenuItem(mi.id); setFieldErrors(p => ({ ...p, menuItemIds: '' })); }} />
                                  {mi.name}
                                </label>
                              );
                            })}
                        </div>
                      )}
                      {menuItemIds.length > 0 && (
                        <span className="mi-hint" style={{ marginTop: 4 }}>
                          {menuItemIds.length} item{menuItemIds.length !== 1 ? 's' : ''} selected
                        </span>
                      )}
                      {fieldErrors.menuItemIds && <span className="mi-err" style={{ marginTop: 4 }}>{fieldErrors.menuItemIds}</span>}
                    </div>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default HappyHourCreatePage;
